import {useContext, useState} from 'react';
import {Linking, ScrollView, StyleSheet, View} from 'react-native';
import {
  Button,
  DataTable,
  IconButton,
  Text,
  Title,
  TouchableRipple,
} from 'react-native-paper';
import {useMutation, useQueryClient} from 'react-query';
import firestore from '@react-native-firebase/firestore';

import {UserContext} from '../App';
import {ReservationEditor} from '../components/FormikStoreEditors/ReservationEditor';
import {
  CreditTransaction,
  filterCreditTransactions,
  filterLimitedPartners,
  filterReservations,
  LimitedPartner,
  Reservation,
  useCreditTransactions,
  useCreditTypes,
  useFetchLimitedPartners,
  useMembers,
  useReservations,
  useReservationYears,
} from '../hooks/useDomainData';
import {PersonInfo, useAuthPersonInfo, usePeople} from '../hooks/usePersonInfo';
import {useAppSettings} from '../components/AppSettings';

function Reservations() {
  const user = useContext(UserContext);
  let reservations = useReservations();
  const creditTypes = useCreditTypes();
  let limitedPartners = useFetchLimitedPartners();
  const members = useMembers();
  const people = usePeople();
  const authPersonInfo = useAuthPersonInfo();
  const creditTransactions = useCreditTransactions();
  const reservationYears = useReservationYears();

  const [resDialogVisible, setResDialogVisible] = useState(false);
  const [currentReservation, setCurrentReservation] = useState<
    Reservation | undefined
  >();

  let loading = true;

  if (!authPersonInfo?.isAdmin && !authPersonInfo?.isGuestRelations) {
    limitedPartners = filterLimitedPartners(
      limitedPartners,
      members,
      authPersonInfo?.person.personId ? [authPersonInfo.person.personId] : [],
    );
    console.log('transactions limitedpartner size ' + limitedPartners.length);

    // filter down to just the ones for our current limited partner(s)
    reservations = reservations?.filter(res =>
      limitedPartners?.find(
        lp => lp.limitedPartnerId === res.limitedPartner?.limitedPartnerId,
      ),
    );
  }

  if (
    creditTransactions &&
    reservations &&
    limitedPartners &&
    members &&
    people &&
    authPersonInfo
  ) {
    loading = false;
  }

  // console.log('all associated LPs: ' + allLimitedPartners.size);
  const filteredTxns = filterCreditTransactions(
    creditTransactions ? Array.from(creditTransactions) : [],
    creditTypes ? Array.from(creditTypes) : [],
    limitedPartners ?? [],
    members ? Array.from(members) : [],
    people ? Array.from(people) : [],
    reservationYears ? Array.from(reservationYears) : undefined,
  );

  const filteredReservations = filterReservations(
    reservations ? Array.from(reservations) : undefined,
    limitedPartners,
    members ? Array.from(members) : undefined,
    people ? Array.from(people) : undefined,
    reservationYears ? Array.from(reservationYears) : undefined,
  );

  const handleCreateEditReservation = (reservation?: Reservation) => {
    // console.log('should edit reservation: ' + reservation?.ref.path);
    setCurrentReservation(reservation);
    setResDialogVisible(true);
  };

  if (!user) {
    return null;
  }

  return (
    <>
      <View style={styles.horizontalCentered}>
        <Title style={styles.title}>
          Reservations, Current Reservation Year
        </Title>
        {(authPersonInfo?.isAdmin || authPersonInfo?.isGuestRelations) && (
          <Button mode="outlined" onPress={() => handleCreateEditReservation()}>
            Create Reservation
          </Button>
        )}
      </View>
      <ScrollView style={[styles.content]}>
        <ReservationList
          loading={loading}
          transactions={filteredTxns}
          reservations={filteredReservations}
          limitedPartners={limitedPartners}
          authPersonInfo={authPersonInfo}
        />
      </ScrollView>
      <ReservationEditor
        allLimitedPartners={Array.from(limitedPartners ?? [])}
        dialogVisible={resDialogVisible}
        setDialogVisible={(visible: boolean) => {
          setResDialogVisible(visible);
          if (!visible) {
            setCurrentReservation(undefined);
          }
        }}
        transactions={filteredTxns}
        reservation={currentReservation}
      />
    </>
  );
}

export interface ReservationListProps {
  loading: boolean;
  transactions?: readonly CreditTransaction[];
  reservations?: readonly Reservation[];
  limitedPartners?: readonly LimitedPartner[];
  authPersonInfo?: PersonInfo;
}

export function ReservationList(props: ReservationListProps) {
  const queryClient = useQueryClient();
  const appSettings = useAppSettings();
  const reservationYears = useReservationYears();

  const [resDialogVisible, setResDialogVisible] = useState(false);
  const [currentReservation, setCurrentReservation] = useState<
    Reservation | undefined
  >();

  const getReservationYearName = (reservation: Reservation): string => {
    // console.log(
    //   `getReservationYearName working with ${reservation.reservationStartJSDate?.valueOf()}`,
    // );
    const reservationYear = reservationYears?.find(year => {
      //   console.log(
      //     `reservationYear ${year.name} start ${year.startJSDate?.valueOf()}`,
      //   );
      // console.log(
      //   'res start - year start: ' +
      //     (reservation.reservationStartJSDate?.valueOf() -
      //       year.startJSDate?.valueOf()),
      // );
      if (
        reservation.reservationStartJSDate &&
        year.startJSDate &&
        reservation.reservationStartJSDate?.valueOf() >
          year.startJSDate?.valueOf()
      ) {
        return year;
      }
    });
    // console.log(`returning: ${reservationYear?.name}`);
    return reservationYear?.name ?? '';
  };

  const handleDeleteReservation = useMutation(
    (reservation: Reservation) => {
      console.log('should delete reservation: ' + reservation.ref.path);
      return firestore()
        .collection('reservations')
        .doc(reservation.ref.id)
        .delete();
    },
    {
      onSettled: () => {
        console.log('deleting reservation succeeded');
        return queryClient.invalidateQueries('reservations');
      },
    },
  );

  const handleCreateEditReservation = (reservation?: Reservation) => {
    // console.log('should edit reservation: ' + reservation?.ref.path);
    setCurrentReservation(reservation);
    setResDialogVisible(true);
  };

  const themedStyles = StyleSheet.create({
    primaryTopBorder: {
      borderTopWidth: 1,
      borderTopColor: appSettings.currentTheme.colors.primary,
    },
  });

  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',

    // These options can be used to round to whole numbers.
    // trailingZeroDisplay: 'stripIfInteger',
  });

  return (
    <>
      <DataTable>
        <DataTable.Header>
          <DataTable.Title>Start Date</DataTable.Title>
          <DataTable.Title>End Date</DataTable.Title>
          <DataTable.Title>Partner ID/Name</DataTable.Title>
          <DataTable.Title>Property</DataTable.Title>
          <DataTable.Title>Credits</DataTable.Title>
          {(props.authPersonInfo?.isAdmin ||
            props.authPersonInfo?.isGuestRelations) && (
            <DataTable.Title>Actions</DataTable.Title>
          )}
        </DataTable.Header>

        {(!props.authPersonInfo || !props.reservations || props.loading) && (
          <DataTable.Row>
            <DataTable.Cell>loading...</DataTable.Cell>
          </DataTable.Row>
        )}
        {props.authPersonInfo &&
          !props.loading &&
          props.reservations?.length === 0 && (
            <DataTable.Row>
              <DataTable.Cell>No reservations found</DataTable.Cell>
            </DataTable.Row>
          )}
        {props.authPersonInfo &&
          (props.reservations?.length ?? 0) > 0 &&
          props.reservations?.map(res => (
            <View key={res.reservationId}>
              <DataTable.Row
                key={`${res.reservationId}er`}
                style={themedStyles.primaryTopBorder}
                onPress={() => handleCreateEditReservation(res)}>
                <DataTable.Cell>
                  {new Date(
                    (res.reservationStartJSDate?.getTime() ?? 0) -
                      (res.reservationStartJSDate?.getTimezoneOffset() ?? 0) *
                        -60000,
                  )?.toDateString()}
                </DataTable.Cell>
                <DataTable.Cell>
                  {new Date(
                    (res.reservationEndJSDate?.getTime() ?? 0) -
                      (res.reservationEndJSDate?.getTimezoneOffset() ?? 0) *
                        -60000,
                  )?.toDateString()}
                </DataTable.Cell>
                <DataTable.Cell>
                  {res.limitedPartner?.limitedPartnerId}){' '}
                  {res.limitedPartner?.limitedPartnerName}
                </DataTable.Cell>
                <DataTable.Cell>{res.property?.propertyName}</DataTable.Cell>
                <DataTable.Cell>
                  {res.requiredCredits}
                  {res.status === 'CANCELED' ? ' (late cancelation)' : ''}
                </DataTable.Cell>
                {(props.authPersonInfo?.isAdmin ||
                  props.authPersonInfo?.isGuestRelations) && (
                  <DataTable.Cell>
                    <>
                      {(props.authPersonInfo?.isAdmin ||
                        props.authPersonInfo?.isGuestRelations) && (
                        <IconButton
                          icon="pencil"
                          size={18}
                          color={appSettings.currentTheme.colors.primary}
                          onPress={() => handleCreateEditReservation(res)}
                        />
                      )}
                      {props.authPersonInfo?.isAdmin && (
                        <IconButton
                          icon="delete"
                          size={18}
                          color={appSettings.currentTheme.colors.primary}
                          onPress={() => handleDeleteReservation.mutate(res)}
                        />
                      )}
                      {(props.authPersonInfo?.isAdmin ||
                        props.authPersonInfo?.isGuestRelations) &&
                        res.guestyReservationId !== undefined && (
                          <TouchableRipple
                            onPress={() =>
                              Linking.openURL(
                                `https://app.guesty.com/reservations/${res.guestyReservationId}/summary`,
                              )
                            }>
                            <Text
                              style={{
                                color: appSettings.currentTheme.colors.primary,
                              }}>
                              Guesty
                            </Text>
                          </TouchableRipple>
                        )}
                    </>
                  </DataTable.Cell>
                )}
              </DataTable.Row>
              {res.guestyTotalFees !== undefined && (
                <DataTable.Row key={`${res.reservationId}guesty`}>
                  <DataTable.Cell>Credit Year:</DataTable.Cell>
                  <DataTable.Cell>{getReservationYearName(res)}</DataTable.Cell>
                  <DataTable.Cell>
                    Total Fees {formatter.format(res.guestyTotalFees)}
                  </DataTable.Cell>
                  <DataTable.Cell>
                    Due Now {formatter.format(res.guestyPaymentsDue)}
                  </DataTable.Cell>
                  <DataTable.Cell>
                    Balance {formatter.format(res.guestyBalanceDue)}
                  </DataTable.Cell>
                  {(props.authPersonInfo?.isAdmin ||
                    props.authPersonInfo?.isGuestRelations) && (
                    <DataTable.Cell>&nbsp;</DataTable.Cell>
                  )}
                </DataTable.Row>
              )}
            </View>
          ))}
      </DataTable>
      <ReservationEditor
        allLimitedPartners={Array.from(props.limitedPartners ?? [])}
        dialogVisible={resDialogVisible}
        setDialogVisible={(visible: boolean) => {
          setResDialogVisible(visible);
          if (!visible) {
            setCurrentReservation(undefined);
          }
        }}
        transactions={props.transactions}
        reservation={currentReservation}
      />
    </>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    position: 'relative',
    backgroundColor: '#fff',
  },
  content: {
    paddingHorizontal: 20,
  },
  profile: {
    marginTop: -50,
    paddingVertical: 10,
  },
  avatar: {
    borderColor: '#fff',
    borderWidth: 5,
    elevation: 4,
  },
  providers: {
    backgroundColor: '#F6F7F8',
    elevation: 4,
    flexDirection: 'row',
    justifyContent: 'space-evenly',
    marginVertical: 30,
    padding: 20,
  },
  fab: {
    position: 'absolute',
    margin: 16,
    right: 0,
    bottom: 0,
  },
  center: {
    width: '100%',
    alignItems: 'center',
  },
  title: {
    padding: 10,
    paddingHorizontal: 20,
  },
  horizontalCentered: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    alignContent: 'center',
  },
});

export default Reservations;
